import { create } from 'zustand'

// Real-time tracking state
interface TrackingState {
  orders: Map<string, OrderTracking>
  drivers: Map<string, DriverInfo>
  activeConnections: Set<string>
  updateOrderLocation: (orderId: string, location: GeoPoint) => void
  updateDriverStatus: (driverId: string, status: DriverStatus) => void
  // FIX (2026-09-13, Issue #5 Tier 2): updates a driver's currentLocation
  // without touching status. Used by useWebSocket.handleDriverUpdate and
  // handleLocationUpdate to forward the `location` field from server
  // payloads (driver.controller.ts:421, 510) that the previous handler
  // silently dropped — driver pins now move on the LiveMap.
  updateDriverLocation: (driverId: string, location: GeoPoint) => void
  addConnection: (connectionId: string) => void
  removeConnection: (connectionId: string) => void
  // FIX (2026-09-13, Issue #5 Tier 1): bulk pre-seed actions so the
  // LiveMap renders from REST immediately on OrderTracker mount, before
  // the first WS event lands. Merge semantics: WS updates that arrive
  // later still win by lastUpdate.
  setOrders: (orders: OrderTracking[]) => void
  setDrivers: (drivers: DriverInfo[]) => void
}

interface OrderTracking {
  orderId: string
  customerId: string
  status: 'pending' | 'assigned' | 'picked_up' | 'in_transit' | 'delivered'
  driverId?: string
  currentLocation?: GeoPoint
  estimatedArrival: Date
  route: GeoPoint[]
  lastUpdate: Date
}

interface DriverInfo {
  id: string
  name: string
  phone: string
  avatar?: string
  status: DriverStatus
  currentLocation?: GeoPoint
  assignedOrders: string[]
  rating: number
  vehicleInfo: VehicleInfo
}

interface VehicleInfo {
  type: 'bike' | 'car' | 'van'
  licensePlate: string
  color: string
  model: string
}

type DriverStatus = 'available' | 'busy' | 'offline' | 'break'

interface GeoPoint {
  lat: number
  lng: number
  timestamp: Date
}

export const useTrackingStore = create<TrackingState>((set, get) => ({
  orders: new Map(),
  drivers: new Map(),
  activeConnections: new Set(),

  updateOrderLocation: (orderId: string, location: GeoPoint) => {
    set((state) => {
      const newOrders = new Map(state.orders)
      const order = newOrders.get(orderId)
      if (order) {
        newOrders.set(orderId, {
          ...order,
          currentLocation: location,
          lastUpdate: new Date(),
          route: [...order.route, location]
        })
      }
      return { orders: newOrders }
    })
  },

  updateDriverStatus: (driverId: string, status: DriverStatus) => {
    set((state) => {
      const newDrivers = new Map(state.drivers)
      const driver = newDrivers.get(driverId)
      if (driver) {
        newDrivers.set(driverId, { ...driver, status })
      }
      return { drivers: newDrivers }
    })
  },

  // FIX (2026-09-13, Issue #5 Tier 2): forward driver GPS updates so the
  // LiveMap truck markers actually move. Purely additive — does not touch
  // status, does not append to any driver-route array (driver routes are
  // not tracked — only order routes are).
  updateDriverLocation: (driverId: string, location: GeoPoint) => {
    set((state) => {
      const next = new Map(state.drivers)
      const driver = next.get(driverId)
      if (driver) {
        next.set(driverId, {
          ...driver,
          currentLocation: location
        })
      }
      return { drivers: next }
    })
  },

  // FIX (2026-09-13, Issue #5 Tier 1): bulk pre-seed from REST. Map merge
  // — if the store already has a fresher entry (later lastUpdate), don't
  // overwrite, so WS updates win once they start arriving.
  setOrders: (incoming: OrderTracking[]) => {
    set((state) => {
      const next = new Map(state.orders)
      for (const o of incoming) {
        const existing = next.get(o.orderId)
        if (!existing || existing.lastUpdate < o.lastUpdate) {
          next.set(o.orderId, o)
        }
      }
      return { orders: next }
    })
  },

  setDrivers: (incoming: DriverInfo[]) => {
    set((state) => {
      const next = new Map(state.drivers)
      for (const d of incoming) {
        next.set(d.id, d)
      }
      return { drivers: next }
    })
  },

  addConnection: (connectionId: string) => {
    set((state) => ({
      activeConnections: new Set(state.activeConnections).add(connectionId)
    }))
  },

  removeConnection: (connectionId: string) => {
    set((state) => {
      const newConnections = new Set(state.activeConnections)
      newConnections.delete(connectionId)
      return { activeConnections: newConnections }
    })
  }
}))

// Inventory management state
interface InventoryState {
  items: Map<string, InventoryItem>
  lowStockAlerts: LowStockAlert[]
  autoReorderRules: AutoReorderRule[]
  freshnessTracking: Map<string, FreshnessData>
  updateStock: (itemId: string, quantity: number, operation: 'add' | 'subtract' | 'set') => void
  addLowStockAlert: (alert: LowStockAlert) => void
  setAutoReorderRule: (rule: AutoReorderRule) => void
  updateFreshness: (itemId: string, freshnessData: FreshnessData) => void
}

interface InventoryItem {
  id: string
  name: string
  category: string
  sku: string
  currentStock: number
  minimumStock: number
  reorderPoint: number
  supplierInfo: SupplierInfo
  location: string
  lastRestocked: Date
  costPerUnit: number
  freshnessPeriod?: number // days
}

interface LowStockAlert {
  id: string
  itemId: string
  currentStock: number
  minimumStock: number
  severity: 'warning' | 'critical'
  created: Date
  acknowledged: boolean
  message?: string
  productName?: string
  productSku?: string
}

interface AutoReorderRule {
  id: string
  itemId: string
  supplierId: string
  reorderQuantity: number
  triggerLevel: number
  isActive: boolean
  lastTriggered?: Date
}

interface SupplierInfo {
  id: string
  name: string
  contact: string
  leadTime: number
  minimumOrder: number
}

interface FreshnessData {
  purchaseDate: Date
  expiryDate: Date
  qualityScore: number // 1-10
  lastChecked: Date
}

export const useInventoryStore = create<InventoryState>((set, get) => ({
  items: new Map(),
  lowStockAlerts: [],
  autoReorderRules: [],
  freshnessTracking: new Map(),

  updateStock: (itemId: string, quantity: number, operation: 'add' | 'subtract' | 'set') => {
    set((state) => {
      const newItems = new Map(state.items)
      const item = newItems.get(itemId)
      if (item) {
        let newQuantity = item.currentStock
        switch (operation) {
          case 'add':
            newQuantity += quantity
            break
          case 'subtract':
            newQuantity -= quantity
            break
          case 'set':
            newQuantity = quantity
            break
        }
        newItems.set(itemId, { ...item, currentStock: Math.max(0, newQuantity) })
        
        // Check for low stock alerts
        if (newQuantity <= item.minimumStock) {
          const alert: LowStockAlert = {
            id: Date.now().toString(),
            itemId,
            currentStock: newQuantity,
            minimumStock: item.minimumStock,
            severity: newQuantity === 0 ? 'critical' : 'warning',
            created: new Date(),
            acknowledged: false
          }
          return { 
            items: newItems, 
            lowStockAlerts: [...state.lowStockAlerts, alert]
          }
        }
      }
      return { items: newItems }
    })
  },

  addLowStockAlert: (alert: LowStockAlert) => {
    set((state) => ({
      lowStockAlerts: [...state.lowStockAlerts, alert]
    }))
  },

  setAutoReorderRule: (rule: AutoReorderRule) => {
    set((state) => ({
      autoReorderRules: state.autoReorderRules.filter(r => r.id !== rule.id).concat(rule)
    }))
  },

  updateFreshness: (itemId: string, freshnessData: FreshnessData) => {
    set((state) => ({
      freshnessTracking: new Map(state.freshnessTracking).set(itemId, freshnessData)
    }))
  }
}))

// Loyalty program state
interface LoyaltyState {
  customers: Map<string, CustomerLoyalty>
  rewardPrograms: RewardProgram[]
  campaigns: LoyaltyCampaign[]
  referrals: Map<string, ReferralData>
  updateCustomerPoints: (customerId: string, points: number, operation: 'add' | 'subtract') => void
  redeemReward: (customerId: string, rewardId: string) => void
  addReferral: (referrerId: string, refereeId: string) => void
}

interface CustomerLoyalty {
  customerId: string
  points: number
  tier: 'bronze' | 'silver' | 'gold' | 'platinum'
  totalSpent: number
  totalOrders: number
  joinDate: Date
  lastActivity: Date
  availableRewards: string[]
  redeemedRewards: RedemptionHistory[]
}

interface RewardProgram {
  id: string
  name: string
  description: string
  pointsCost: number
  rewardType: 'discount' | 'product' | 'service'
  value: number
  isActive: boolean
  validUntil?: Date
  usageLimit?: number
  currentUsage: number
}

interface LoyaltyCampaign {
  id: string
  name: string
  type: 'birthday' | 'anniversary' | 'seasonal' | 'milestone'
  trigger: CampaignTrigger
  reward: RewardProgram
  isActive: boolean
  startDate: Date
  endDate: Date
  participantCount: number
}

interface CampaignTrigger {
  type: 'date' | 'purchase_amount' | 'order_count'
  value: string | number | Date
  comparison?: 'equals' | 'greater_than' | 'less_than'
}

interface ReferralData {
  referrerId: string
  refereeId: string
  status: 'pending' | 'completed' | 'rewarded'
  referralCode: string
  createdDate: Date
  completedDate?: Date
  rewardGiven: boolean
}

interface RedemptionHistory {
  rewardId: string
  redeemedAt: Date
  pointsUsed: number
  orderReference?: string
}

export const useLoyaltyStore = create<LoyaltyState>((set, get) => ({
  customers: new Map(),
  rewardPrograms: [],
  campaigns: [],
  referrals: new Map(),

  updateCustomerPoints: (customerId: string, points: number, operation: 'add' | 'subtract') => {
    set((state) => {
      const newCustomers = new Map(state.customers)
      const customer = newCustomers.get(customerId)
      if (customer) {
        const newPoints = operation === 'add' 
          ? customer.points + points 
          : Math.max(0, customer.points - points)
        
        // Update tier based on total spent
        let tier: 'bronze' | 'silver' | 'gold' | 'platinum' = 'bronze'
        if (customer.totalSpent >= 10000) tier = 'platinum'
        else if (customer.totalSpent >= 5000) tier = 'gold'
        else if (customer.totalSpent >= 1000) tier = 'silver'

        newCustomers.set(customerId, {
          ...customer,
          points: newPoints,
          tier,
          lastActivity: new Date()
        })
      }
      return { customers: newCustomers }
    })
  },

  redeemReward: (customerId: string, rewardId: string) => {
    set((state) => {
      const customer = state.customers.get(customerId)
      const reward = state.rewardPrograms.find(r => r.id === rewardId)
      
      if (customer && reward && customer.points >= reward.pointsCost) {
        const newCustomers = new Map(state.customers)
        const redemption: RedemptionHistory = {
          rewardId,
          redeemedAt: new Date(),
          pointsUsed: reward.pointsCost
        }
        
        newCustomers.set(customerId, {
          ...customer,
          points: customer.points - reward.pointsCost,
          redeemedRewards: [...customer.redeemedRewards, redemption],
          lastActivity: new Date()
        })
        
        return { customers: newCustomers }
      }
      return state
    })
  },

  addReferral: (referrerId: string, refereeId: string) => {
    const referralCode = `REF${Date.now()}`
    const referralData: ReferralData = {
      referrerId,
      refereeId,
      status: 'pending',
      referralCode,
      createdDate: new Date(),
      rewardGiven: false
    }
    
    set((state) => ({
      referrals: new Map(state.referrals).set(referralCode, referralData)
    }))
  }
}))

// Analytics state
interface AnalyticsState {
  revenueData: RevenueData[]
  customerLifetimeValue: Map<string, CLVData>
  forecastData: ForecastData[]
  kpis: KPIMetrics
  updateRevenue: (data: RevenueData) => void
  updateCLV: (customerId: string, clv: CLVData) => void
  updateForecast: (forecast: ForecastData[]) => void
  updateKPIs: (kpis: Partial<KPIMetrics>) => void
}

interface RevenueData {
  date: string
  revenue: number
  orders: number
  averageOrderValue: number
  costs: number
  profit: number
  category?: string
}

interface CLVData {
  customerId: string
  currentValue: number
  predictedValue: number
  averageOrderValue: number
  orderFrequency: number
  lifespanMonths: number
  lastCalculated: Date
}

interface ForecastData {
  period: string
  predictedRevenue: number
  predictedOrders: number
  confidence: number
  seasonalFactor: number
}

interface KPIMetrics {
  dailyRevenue: number
  weeklyRevenue: number
  monthlyRevenue: number
  yearlyRevenue: number
  averageOrderValue: number
  customerAcquisitionCost: number
  customerRetentionRate: number
  inventoryTurnover: number
  deliveryPerformance: number
  customerSatisfaction: number
  lastUpdated: Date
}

export const useAnalyticsStore = create<AnalyticsState>((set, get) => ({
  revenueData: [],
  customerLifetimeValue: new Map(),
  forecastData: [],
  kpis: {
    dailyRevenue: 0,
    weeklyRevenue: 0,
    monthlyRevenue: 0,
    yearlyRevenue: 0,
    averageOrderValue: 0,
    customerAcquisitionCost: 0,
    customerRetentionRate: 0,
    inventoryTurnover: 0,
    deliveryPerformance: 0,
    customerSatisfaction: 0,
    lastUpdated: new Date()
  },

  updateRevenue: (data: RevenueData) => {
    set((state) => ({
      revenueData: [...state.revenueData.filter(r => r.date !== data.date), data]
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    }))
  },

  updateCLV: (customerId: string, clv: CLVData) => {
    set((state) => ({
      customerLifetimeValue: new Map(state.customerLifetimeValue).set(customerId, clv)
    }))
  },

  updateForecast: (forecast: ForecastData[]) => {
    set({ forecastData: forecast })
  },

  updateKPIs: (newKPIs: Partial<KPIMetrics>) => {
    set((state) => ({
      kpis: { ...state.kpis, ...newKPIs, lastUpdated: new Date() }
    }))
  }
}))